import {
  applyOperation,
  formatEquation,
  substitute,
  type AlgebraOperation,
  type Equation,
} from '../../shared/algebra.ts';
import type { PackageTeachingBeat } from '../../shared/types.ts';
import type { AudioSegment } from '../components/NarrationPlayer.tsx';
import { buildAlgebraTeachingPlan } from './algebraTeachingPlan.ts';
import type { TeachingPlan } from './teachingTimeline.ts';
import {
  phraseTime,
  spokenWord,
  type Focus,
  type SceneFrame,
  type SceneKind,
} from './lessonScene.ts';

type Word = AudioSegment['words'][number];
export type PlannedTeachingCue = NonNullable<
  PackageTeachingBeat['cues']
>[number] & { phaseIndex: number; at: number; endAt: number };
export type PackageTeachingPlan = {
  kind: SceneKind;
  durationMs: number;
  cues: PlannedTeachingCue[];
};
export type PackageTeachingFrame = {
  frame: SceneFrame;
  equation: Equation;
  operation?: AlgebraOperation;
  operationProgress: number;
  candidate?: string;
};

const focusMap: Record<PackageTeachingBeat['focus'], Focus> = {
  unknown: 'x',
  equality: 'equals',
  both: 'both',
  groups: 'coefficient',
  check: 'result',
  story: 'both',
};
const kindFor = (beat: PackageTeachingBeat): SceneKind =>
  beat.visual?.kind === 'operation'
    ? beat.visual.operation?.kind === 'divide'
      ? 'divide'
      : 'subtract'
    : beat.visual?.kind === 'check'
      ? 'check'
      : beat.visual?.kind === 'story'
        ? 'story'
        : 'intro';
const clamp = (value: number) => Math.max(0, Math.min(1, value));

/** Resolve authored phrases against the real MP3 word alignment. */
export function packageTeachingPlan(
  beat: PackageTeachingBeat,
  words: readonly Word[],
  durationMs: number,
): PackageTeachingPlan {
  const authored = beat.cues?.length
    ? [...beat.cues]
    : [
        {
          phrase: '',
          phase: 'start',
          focus: beat.focus,
          caption: beat.takeaway,
        },
      ];
  // Compatibility for the pinned first rental package: its recording contains
  // this result sentence, but its cue list only declared the division phase.
  // Keep package bytes/digests stable for existing learner sessions.
  if (beat.id === 'share-equal-groups' && beat.visual?.operation?.kind === 'divide' && beat.narration.includes('So x equals three.') && !authored.some(c => c.phase === 'result')) {
    authored.push({ phrase: 'So x equals three', phase: 'result', focus: 'groups', caption: 'Each equal group is three. x = 3.' });
  }
  if (beat.id === 'subtract-from-both' && beat.narration.includes('Now two x equals six.') && !authored.some(c => c.phase === 'result')) {
    authored.push({ phrase: 'Now two x equals six', phase: 'result', focus: 'both', caption: 'The four extra units are removed from both sides. 2x = 6.' });
  }
  if (beat.id === 'check-the-original' && beat.narration.includes('Replace each x with three.') && authored.length === 1) {
    authored.splice(0, authored.length,
      { phrase: '', phase: 'original', focus: 'equality', caption: 'Return to the original equation.' },
      { phrase: 'Replace each x with three', phase: 'substitution', focus: 'unknown', caption: 'Replace every x with 3.' },
      { phrase: 'Two times three is six', phase: 'products', focus: 'both', caption: 'Multiply first, then add four.' },
      { phrase: 'Both sides are ten', phase: 'check', focus: 'equality', caption: '10 = 10. Three makes the original equation true.' });
  }
  const phases = new Map<string, number>();
  const starts = authored.map((cue, index) => {
    if (!phases.has(cue.phase)) phases.set(cue.phase, phases.size);
    return {
      ...cue,
      phaseIndex: phases.get(cue.phase)!,
      at: cue.phrase
        ? phraseTime(words, cue.phrase, index === 0 ? 0 : Infinity)
        : 0,
    };
  });
  return {
    kind: kindFor(beat),
    durationMs,
    cues: starts.map((cue, index) => ({
      ...cue,
      endAt: Math.max(cue.at + 1, starts[index + 1]?.at ?? durationMs),
    })),
  };
}

const numberWords: Record<string, string> = {
  zero: '0',
  one: '1',
  two: '2',
  three: '3',
  four: '4',
  five: '5',
  six: '6',
  seven: '7',
  eight: '8',
  nine: '9',
  ten: '10',
  eleven: '11',
  twelve: '12',
  thirteen: '13',
  fourteen: '14',
  fifteen: '15',
  sixteen: '16',
  seventeen: '17',
  eighteen: '18',
  nineteen: '19',
  twenty: '20',
};
const numericFocus = (
  word: string,
  equation: Equation,
  candidate: string | undefined,
  fallback: Focus,
): Focus => {
  const token = word.toLowerCase().replace(/[$.,:;!?]/g, '');
  if (token === 'x') return 'x';
  if (token === 'equal' || token === 'equals') return 'equals';
  const value = numberWords[token] ?? token;
  if (candidate === value) return 'x';
  if (equation.left.x === value) return 'coefficient';
  if (equation.left.constant === value) return 'constant';
  if (equation.right.constant === value) return 'total';
  return fallback;
};

/** Build all visual state from media time, so pause, seek, and replay are stable. */
export function packageTeachingFrame(
  beat: PackageTeachingBeat,
  segment: AudioSegment | undefined,
  positionMs: number,
  equationOverride?: Equation,
  reduced = false,
): PackageTeachingFrame {
  const visual = beat.visual,
    original =
      equationOverride ??
      visual?.originalEquation ??
      beat.before ??
      beat.equation;
  const words = segment?.words ?? [],
    duration = Math.max(1, segment?.durationMs ?? 1),
    position = Math.max(0, Math.min(positionMs, duration));
  const plan = packageTeachingPlan(beat, words, duration),
    active = plan.cues.filter((cue) => position >= cue.at).at(-1) ?? {
      ...plan.cues[0],
      phase: 'original',
      phaseIndex: 0,
      at: 0,
      focus: 'both' as const,
      caption: beat.takeaway,
    };
  const phaseStart =
    plan.cues.find((cue) => cue.phaseIndex === active.phaseIndex)?.at ??
    active.at;
  const next = plan.cues.find(
    (cue) => cue.phaseIndex > active.phaseIndex && Number.isFinite(cue.at),
  );
  const phaseEnd = Math.max(phaseStart + 1, next?.at ?? duration),
    progress = reduced
      ? 1
      : clamp((position - phaseStart) / (phaseEnd - phaseStart));
  const operation = visual?.kind === 'operation' ? visual.operation : undefined;
  const operationWord =
    operation?.kind === 'subtract'
      ? 'subtract'
      : operation?.kind === 'divide'
        ? 'divid'
        : operation?.kind;
  const operationCueIndex = operationWord
    ? Math.max(
        0,
        plan.cues.findIndex((cue) =>
          `${cue.phase} ${cue.phrase} ${cue.caption}`
            .toLowerCase()
            .includes(operationWord),
        ),
      )
    : 0;
  const operationStart = plan.cues[operationCueIndex]?.at ?? 0;
  const resultCue = plan.cues.find((c) =>
    /^(result|complete|simplified)$/.test(c.phase),
  );
  const operationEnd = Math.max(operationStart + 1, resultCue?.at ?? duration);
  const middle =
    plan.cues.find((c) => /^(cancel|factor|simplify)$/.test(c.phase))?.at ??
    (operationStart + operationEnd) / 2;
  const operationProgress =
    position >= operationEnd
      ? 1
      : position < operationStart
        ? 0
        : position < middle
          ? 0.18 +
            0.31 *
              clamp(
                (position - operationStart) /
                  Math.max(1, middle - operationStart),
              )
          : 0.5 +
            0.29 *
              clamp((position - middle) / Math.max(1, operationEnd - middle));
  const result = operation
      ? applyOperation(original, operation)
      : beat.equation,
    equation =
      visual?.kind === 'check' || (operation && operationProgress < 1)
        ? original
        : result;
  const candidate = visual?.kind === 'check' ? visual.candidate : undefined,
    word = spokenWord(words, position),
    fallback =
      focusMap[active.focus as keyof typeof focusMap] ??
      (active.focus as Focus);
  const semanticCheckPhase: Record<string, number> = {
    original: 0,
    start: 0,
    substitution: 1,
    replace: 1,
    products: 3,
    sum: 3,
    check: 4,
    result: 4,
  };
  const phase =
    visual?.kind === 'check'
      ? (semanticCheckPhase[active.phase] ?? active.phaseIndex)
      : active.phaseIndex;
  const resolvedFocus = numericFocus(word, original, candidate, fallback);
  const frameEquation =
    candidate && visual
      ? (() => {
          const checked = substitute(visual.originalEquation, candidate);
          return phase >= 4
            ? `${checked.left} = ${checked.right}`
            : formatEquation(visual.originalEquation);
        })()
      : formatEquation(equation);
  return {
    frame: {
      kind: plan.kind,
      phase,
      progress,
      focus: resolvedFocus,
      pointerFocus: resolvedFocus,
      caption: active.caption,
      word,
      equation: frameEquation,
    },
    equation,
    operation,
    operationProgress,
    candidate,
  };
}

/** The paper and tiles share the authored semantic cue clock. */
export function packageWrittenPlan(
  beat: PackageTeachingBeat,
  segment: AudioSegment | undefined,
): TeachingPlan {
  const words = segment?.words ?? [],
    duration = segment?.durationMs ?? 0;
  const visual = beat.visual;
  const base = buildAlgebraTeachingPlan(
    visual?.originalEquation ?? beat.before ?? beat.equation,
    visual?.operation,
    words,
    duration,
    visual?.candidate,
  );
  if (!duration || !beat.cues?.length) return base;
  const cues = packageTeachingPlan(beat, words, duration).cues;
  // Do not insert a separately narrated cancellation phase into recordings
  // that never describe it. The fractions still explain the same operation.
  const steps = base.steps.filter(step => step.id !== 'factor' || cues.some(c => c.phase === 'factor') || words.some(w => /^factor[,.]?$/i.test(w.text)));
  const starts = steps.map((step) =>
    step.id === 'original'
      ? 0
      : (cues.find((c) => c.phase === step.id)?.at ?? step.startMs),
  );
  return {
    durationMs: duration,
    steps: steps.map((step, i) => {
      const startMs = Math.min(
          duration,
          Math.max(i ? starts[i - 1] : 0, starts[i]),
        ),
        endMs = Math.max(startMs, starts[i + 1] ?? duration);
      const sentenceEnd = words.find(
        (w) =>
          w.startMs >= startMs &&
          w.endMs <= endMs &&
          /[.!?]["'’”]?$/.test(w.text),
      )?.endMs;
      return { ...step, startMs, endMs, inkEndMs: sentenceEnd ?? endMs };
    }),
  };
}
